import { useEffect, useState } from 'react';
import { fetchAlertLive, fetchWaveReview } from '../api';
import { AlertLive, AlertLiveHz, AlertStage, DebutBoard, DebutCandidate, DebutNeed, WaveReview, WaveItem } from '../types';

// 🛰️ 통계탭 본체(관리자 전용) — **1층 스크롤 3단**(08-03 전면 재설계, 탭 2층 폐지).
//   ① 🎓 승격 심사 — 주인공. "지금 무엇이 화면 승격에 가까운가"를 맨 위에서 한눈에.
//   ② 📡 오늘 — 실전/검증 중 한 표. 화면 알림(화면/검증 중)는 칸 하나로 구분(탭 분리 폐지).
//   ③ 🌊 복기·정밀도 — 접힘(기본 닫힘). 열 때만 파도 복기를 불러온다.
//   ▸ 이전 구조의 문제: 같은 후보가 화면 승격 진행도·검증 중 시험 표·이모지 칩 세 곳에 흩어져 숫자 정의가 제각각,
//     화면 승격 진행도가 하필 "실전 알림" 탭 안에 있고, 판정 기준인 5분이 1/3/10분과 동등하게 보였다.
//   리스킨 토큰(dc-*) 재사용(폰트·mono·스크롤·진입 모션), 통계탭 안에서만. 알림 피드엔 성적 노출 안 함.

const KIND_META: Record<string, { icon: string; label: string; hint?: string }> = {
  knee:       { icon: '🦵', label: '하이닉스 힘 감지', hint: '하이닉스의 사자·팔자 물량과 실제 체결이 한 방향으로 세게 쏠린 순간' },
  flow_buy:   { icon: '🏦', label: '큰손 매수' },
  flow_sell:  { icon: '🏦', label: '큰손 매도' },
  flowearly:  { icon: '🚀', label: '큰손 매수·매도 시작', hint: '확인 전 원본(비교용) — 08-04 밤부터 검증 중이고, 화면은 90초 확인을 거친 쪽이 이어받았어요(성적은 계속 쌓아 둘을 비교해요)' },
  flowearly_confirmed: { icon: '⏩', label: '큰손 매수 시작(90초 확인)', hint: '큰손이 사기 시작한 뒤 90초에도 같은 방향이 이어졌을 때만 울려요 — 조금 늦는 대신 더 정확한 판(잠정). 08-08부터 화면·소리는 매수만(매도는 검증 중으로 채점만)' },
  wave_dip_rebound: { icon: '🌊', label: '급락 반등 후보', hint: '지수 5분 급락 + 하이닉스 체결 폭증 + 매도쏠림이 겹친 순간 — 검증 중(성적 모으는 중)' },
  marketmove: { icon: '⚡', label: '지수 급락 속보', hint: '떨어질 때만 화면에 떠요(오를 때는 소리 없이 성적만 쌓아요)' },
  news:       { icon: '🗞️', label: '뉴스 속보', hint: '방향이 없는 주의 알림 — 울린 횟수·시각만(적중 채점 안 함)' },
  lifeline:   { icon: '🔌', label: '데이터 끊김·복구', hint: '서버가 받는 체결·수급·지수 데이터가 장중에 끊기면/복구되면 알리는 시스템 경보(데이터 연결 감시) — 시장 방향 신호가 아니라 울린 횟수·시각만(적중 채점 안 함)' },
  stockmove:  { icon: '🎯', label: '가격 경보', hint: '하이닉스 ±0.7% 급변 — 07-24 검증 중으로 내림(하이닉스 힘 감지의 대조군)' },
  ensemble:   { icon: '🤝', label: '외국인·연기금 동시 매수', hint: '외국인과 연기금이 같이 순매수를 크게 늘린 순간' },
  flowreact:  { icon: '🐋', label: '큰손 매수 뒤 하이닉스 반응', hint: '코스피 큰손이 산 뒤 하이닉스가 따라 움직인 순간' },
  prime:      { icon: '🎖️', label: '종합 신호 v1', hint: '여러 근거가 한 방향으로 겹친 순간 — 08-01 검증 중으로 내림(승률 51.6%, 기준 70 미달). 채점은 계속' },
  prime_v2:   { icon: '🎖️', label: '종합 신호 v2', hint: '체결이 연달아 터지는 비율·사자팔자 정렬·먼저 오는 미세 움직임·이상하게 조용함 — 이 네 가지를 필수 조건으로 쓰는 2세대' },
};
// ② 표에 놓을 종류와 **기본 순서**. 실제 화면/검증 중 판정은 서버 stage가 준다(프론트가 목록을 또 들면
//    한쪽만 고쳐 조용히 어긋난다 — 70 독트린 때 실제로 겪은 사고). 여긴 정렬 취향일 뿐이다.
// ⚠️ 여기 실리는 건 **분 단위(5/30분) 채점을 받는 종류**만이다. 🌅 아침 판정 5종은 하루 단위(시가→종가),
//   🌙 급락일 밤 반등은 하룻밤 단위(급락일 종가→다음 거래일 시가) 채점이라 이 표에 넣으면 '—'만 뜬다 —
//   그쪽은 위 ① 승격 심사 줄에서 본다(서버 debut_board가 공급).
const TABLE_KINDS = ['marketmove', 'news', 'lifeline', 'prime', 'prime_v2', 'knee',
  'flow_buy', 'flow_sell', 'flowearly', 'flowearly_confirmed', 'wave_dip_rebound',
  'stockmove', 'ensemble', 'flowreact'];
// 알림 기록 미등록(방향이 없어 승률 채점을 안 하는) 종류 — 울림 수·시각만 피드에서 세어 표시한다.
const UNSCORED_KINDS = new Set(['news', 'lifeline']);
const UNSCORED_TIP: Record<string, string> = {
  news: '뉴스는 방향이 없어 적중 채점을 하지 않아요(제목 전달용).',
  lifeline: '데이터 끊김·복구는 시장 방향 신호가 아니라 시스템 경보라 적중 채점을 하지 않아요.',
};
const MINOR: ('m1' | 'm3' | 'm10')[] = ['m1', 'm3', 'm10'];   // 곁다리 지평 — 판정 기준은 5분 하나
const MINOR_LABEL: Record<string, string> = { m1: '1분', m3: '3분', m10: '10분' };

// ── 🎨 규격 V2(08-23) 부품 ─────────────────────────────────────────
// 알림 종류 색 사전 — 단타 피드 노드·범례와 **같은 색**을 통계에서도 쓴다(DESIGN_SYSTEM.md).
//   이모지를 그림 대신 쓰지 않는다 → 종류는 색 점 하나로 구분하고, 글자는 그대로 둔다.
const KIND_DOT: Record<string, string> = {
  flow_buy: 'bg-dc-up', ensemble: 'bg-dc-up',
  flow_sell: 'bg-dc-down',
  flowearly: 'bg-dc-chodong', flowearly_confirmed: 'bg-dc-chodong',
  flowreact: 'bg-dc-react',
  marketmove: 'bg-dc-drop', wave_dip_rebound: 'bg-dc-drop',
  prime: 'bg-dc-prime', prime_v2: 'bg-dc-prime',
  news: 'bg-dc-news',
  lifeline: 'bg-dc-warn',
};
// 승격 심사에는 표에 없는 종류(아침 판정·밤 반등·공시 등)도 올라온다 — 이름 앞머리로 같은 사전을 이어 쓴다.
const KIND_PREFIX: [string, string][] = [
  ['morning', 'bg-dc-morning'], ['night', 'bg-dc-night'], ['disclosure', 'bg-dc-down'],
  ['flowearly', 'bg-dc-chodong'], ['flowreact', 'bg-dc-react'], ['prime', 'bg-dc-prime'],
];
const kindDot = (k?: string) => KIND_DOT[k ?? '']
  ?? KIND_PREFIX.find(([p]) => (k ?? '').startsWith(p))?.[1]
  ?? 'bg-dc-watch';
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';
const GHOST = 'inline-flex items-center h-[22px] px-2.5 rounded-lg border border-[#202A3C] text-dc-ink2/90 text-[11.5px] font-medium transition-colors hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-30 disabled:hover:border-[#202A3C] disabled:hover:text-dc-ink2/90';
const GHOST_ON = 'inline-flex items-center h-[22px] px-2.5 rounded-lg border border-dc-point/35 bg-dc-point/10 text-dc-point text-[11.5px] font-semibold transition-colors';

const STAGE_META: Record<AlertStage, { label: string; cls: string; hint: string }> = {
  screen:    { label: '화면', cls: 'border-dc-point/50 bg-dc-point/10 text-[#EBCA96]',
               hint: '화면·소리로 실제로 나가는 알림이에요.' },
  down_only: { label: '화면·하락만', cls: 'border-dc-point/50 bg-dc-point/10 text-[#EBCA96]',
               hint: '떨어질 때만 화면에 떠요(오를 때는 소리 없이 성적만 쌓아요).' },
  up_only:   { label: '화면·상승만', cls: 'border-dc-point/50 bg-dc-point/10 text-[#EBCA96]',
               hint: '오를 때만 화면에 떠요(떨어질 때는 소리 없이 성적만 쌓아요).' },
  silent:    { label: '검증 중', cls: 'border-dc-line1 bg-dc-bg1/60 text-dc-ink3',
               hint: '화면엔 안 나오고 성적만 쌓는 중이에요 — 기준을 넘기면 화면에 올릴 후보가 돼요.' },
  // 🪦 08-22 정리분 — '검증 중'과 **반드시 갈라 보여야 한다**. '검증 중'은 "아직 재는 중"이라는 약속인데
  //   정리된 종류는 성적이 더 안 쌓이므로, 같은 배지를 쓰면 화면이 거짓말을 한다(위쪽 승격 심사엔 줄도 없다).
  //   아래 숫자들은 **정리되기 전까지 쌓인 기록**이라 지난 날짜를 볼 때 그대로 보인다.
  retired:   { label: '정리됨', cls: 'border-dc-hair bg-dc-base/60 text-[#4A5567] line-through decoration-[#3C4759]',
               hint: '2026-08-22에 정리했어요 — 성적이 기준에 한참 못 미쳐서 이제 울리지도, 채점하지도 않아요. 아래 숫자는 그전까지 쌓인 기록이에요.' },
};
// 🏦 수급류(큰손·큰손 매수 시작)는 방향이 '오르내림'이 아니라 '사고팜'이다 — 같은 up_only라도 말을 바꿔 준다.
//   (⏩큰손 매수 시작은 08-04부터 **매수만** 화면 — 매도는 검증 중. 지수 급변은 그대로 '하락만'.)
function stageMeta(stage: AlertStage, kind?: string) {
  const m = STAGE_META[stage] ?? STAGE_META.silent;
  if (kind?.startsWith('flow') && (stage === 'up_only' || stage === 'down_only')) {
    return stage === 'up_only'
      ? { ...m, label: '화면·매수만', hint: '매수 조짐일 때만 화면·소리로 나가요(매도 조짐은 성적만 쌓아요).' }
      : { ...m, label: '화면·매도만', hint: '매도 조짐일 때만 화면·소리로 나가요(매수 조짐은 성적만 쌓아요).' };
  }
  return m;
}
const WIN_TIP = '울린 방향대로 5분 뒤 갔는지 — 100번 울려서 몇 번 맞았나(%)';

// ── 서식 헬퍼 ──────────────────────────────────────────────────────
function fmtHm(ts: number | null | undefined): string {
  if (!ts) return '—';
  try {
    return new Date(ts * 1000).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Seoul' });
  } catch { return '—'; }
}
const hitCls = (v: number | null) => v == null ? 'text-dc-ink3/70' : v >= 55 ? 'text-dc-up' : v <= 45 ? 'text-dc-down' : 'text-dc-ink2';
const signed = (v: number | null | undefined) => v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`;

function kstNow(): Date { return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' })); }
function isMarketHours(): boolean {
  const k = kstNow();
  const d = k.getDay();
  if (d === 0 || d === 6) return false;
  const m = k.getHours() * 60 + k.getMinutes();
  return m >= 9 * 60 && m <= 15 * 60 + 40;
}
// 지금 장이 어디쯤인가 — 빈 표를 "고장"으로 오해하지 않게 하는 설명에만 쓴다.
function marketPhase(): 'weekend' | 'pre' | 'early' | 'open' | 'closed' {
  const k = kstNow();
  const d = k.getDay();
  if (d === 0 || d === 6) return 'weekend';
  const m = k.getHours() * 60 + k.getMinutes();
  if (m < 9 * 60) return 'pre';
  if (m < 9 * 60 + 30) return 'early';
  if (m <= 15 * 60 + 40) return 'open';
  return 'closed';
}
const EMPTY_COPY: Record<string, string> = {
  weekend: '오늘은 주말이라 장이 안 열려요 — 표가 비어 있는 게 정상이에요.',
  pre: '아직 장 전이에요 — 09시부터 집계가 시작돼요. 고장이 아니에요.',
  early: '장 초반이라 아직 집계 전이에요 — 알림이 울리면 여기부터 쌓입니다. 고장이 아니에요.',
  open: '아직 오늘 울린 알림이 없어요 — 조용한 장이라는 뜻이에요.',
  closed: '오늘은 울린 알림이 없었어요.',
};
function kstDateStr(offsetDays = 0): string {
  const k = kstNow();
  k.setDate(k.getDate() + offsetDays);
  return `${k.getFullYear()}-${String(k.getMonth() + 1).padStart(2, '0')}-${String(k.getDate()).padStart(2, '0')}`;
}
// 📅 날짜 표시·판정 (08-04 날짜 탐색) — '2026-08-04' → '8월 4일(화)'
const _WD = ['일', '월', '화', '수', '목', '금', '토'];
function fmtDay(d: string): string {
  const [y, m, dd] = (d || '').split('-').map(Number);
  if (!y || !m || !dd) return d || '—';
  return `${m}월 ${dd}일(${_WD[new Date(y, m - 1, dd).getDay()]})`;
}
function isWeekendDay(d: string): boolean {
  const [y, m, dd] = (d || '').split('-').map(Number);
  if (!y || !m || !dd) return false;
  const g = new Date(y, m - 1, dd).getDay();
  return g === 0 || g === 6;
}

function StageBadge({ stage, kind, provisional, certified, certifiedOn }:
  { stage: AlertStage; kind?: string; provisional?: boolean; certified?: boolean; certifiedOn?: string | null }) {
  const m = stageMeta(stage, kind);
  // 화면 밖(검증 중·정리됨)엔 '잠정'·'최종 합격' 딱지를 붙이지 않는다 — 둘 다 화면에 오른 알림의 표시다.
  const offScreen = stage === 'silent' || stage === 'retired';
  // 🏷️ 잠정 — 화면엔 올랐지만 아직 최종 합격 전. 화면에 떴다고 검증 끝이 아니라는 표시.
  const prov = !!provisional && !offScreen;
  // 🎓 최종 합격 — 표본 100·승률 70을 채웠거나 용준님이 조기 결재로 확정한 알림.
  const cert = !!certified && !offScreen;
  const tip = prov ? `${m.hint} 아직 시험 중이라 '잠정'이에요 — 최종 합격(표본 100건·승률 70%)은 진행 중.`
    : cert ? `${m.hint} 최종 합격이에요${certifiedOn ? ` (${certifiedOn} 결재)` : ''} — 성적은 그 뒤로도 계속 채점해요.`
      : m.hint;
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded-full border text-[10.5px] font-semibold whitespace-nowrap ${m.cls}`} title={tip}>
      {m.label}{prov && '(임시 합격)'}{cert && '(최종 합격)'}
    </span>
  );
}

// ═══ ① 🎓 승격 심사 — 이 화면의 주인공 ═════════════════════════════
// "화면에 올라오려면 뭐가 남았나"를 후보별 한 덩이로. 승격 조건 3개(승률·표본·늦지 않게 울림)을 다 넘겨야 화면 승격 후보가 된다.
function needText(nd: DebutNeed): string {
  if (nd.what === '표본') return `표본 ${nd.n_left}건 더`;
  if (nd.what === '승률') return `승률 −${nd.gap}%p`;
  if (nd.what === '안늦음') return '늦지 않게 울림 조건';
  if (nd.what === '보류') return '최종 합격 보류(재검토 중)';
  return '기준 통과';
}

// 승격 조건 칩 — 통과는 초록(ok), 미달은 중립. ✅/⏳ 이모지는 08-23 은퇴(색이 그 자리를 대신한다).
function Gate({ ok, label, detail, hint }: { ok: boolean | null; label: string; detail?: string; hint?: string }) {
  return (
    <span className={`inline-flex items-center h-[21px] px-2 rounded-md border text-[11.5px] font-semibold whitespace-nowrap ${ok
      ? 'border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok'
      : 'border-dc-line1 bg-dc-bg1 text-dc-ink3'}`} title={hint}>
      {label}{detail ? ` ${detail}` : ''}
    </span>
  );
}

// 승률 막대 — 채움은 앰버(기준 달성)/회색(미달), 기준선은 세로 1px. 초록·노랑 채움은 08-23 은퇴.
function WinBar({ win, bar }: { win: number | null; bar: number }) {
  const pct = Math.max(0, Math.min(100, win ?? 0));
  const hit = win != null && win >= bar;
  return (
    <div className="relative h-1 w-full max-w-[760px]">
      <div className="absolute inset-0 rounded-full bg-dc-hair overflow-hidden">
        <div className={`h-full rounded-full ${hit ? 'bg-dc-point' : 'bg-[#8E9AB0]'}`}
          style={{ width: `${pct}%` }} />
      </div>
      {/* 70% 기준선 — 이 선을 넘어야 화면 승격 자격 */}
      <div className="absolute -top-[3px] h-[10px] w-px bg-dc-ink3" style={{ left: `${bar}%` }} title={`기준 ${bar}%`} />
    </div>
  );
}

function RaceRow({ c, nMin }: { c: DebutCandidate; nMin: number }) {
  const t = c.trend;
  // 서버가 아직 옛 응답(승격 조건 3개 없음)을 줄 때도 화면이 하얗게 죽지 않게 — 배포 순간의 잠깐 어긋남 대비.
  const g = c.gates ?? { sample: false, win: false, timely: null };
  const tm = c.timely ?? { ok: null, lag: null, mfe: null, note: '계측 중' };
  const early = c.early ?? null;      // ⚡ 조기 판정(실전 표본만)
  const fl = c.fill ?? null;          // 💵 체결가 기준 성적(병기 전용 — 판정에 안 들어간다)
  // 🎞️ 되감기 칸 — 잰 게 하나라도 있을 때만 그린다. 0건짜리 빈 칸을 띄우면
  //   "되감기 0건"이 줄마다 붙어 정작 값이 있는 줄이 안 보인다(없는 칸은 안 그리는 게 정직하다).
  const tp = (fl?.tape && fl.tape.n > 0) ? fl.tape : null;
  const susp = c.suspended ?? null;   // ⏸️ 최종 합격 보류 중인가
  // 🎨 규격 V2(08-23) — 카드 상자를 걷어내고 헤어라인 한 줄로. '통과' 초록 배경 칠갑도 제거(칩 하나로 충분).
  const dim = c.stage_now === 'silent' || c.stage_now === 'retired';
  return (
    <div className="border-t border-dc-hair py-4 space-y-2.5">
      {/* 1줄 — 이름 · 상태 · 5분 승률 */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`w-[9px] h-[9px] rounded-full shrink-0 ${kindDot(c.kind)} ${dim ? 'opacity-60' : ''}`} />
        <b className={`text-[17.5px] font-bold ${dim ? 'text-dc-ink2' : 'text-dc-ink1'}`}>{c.label}</b>
        <StageBadge stage={c.stage_now ?? (c.live ? 'screen' : 'silent')} kind={c.kind}
          provisional={c.provisional} certified={c.certified} certifiedOn={c.certified_on} />
        {c.passed && <span className="inline-flex items-center h-5 px-2 rounded-full border border-dc-ok/40 bg-dc-ok/[0.07] text-dc-ok text-[10.5px] font-semibold"
          title="세 가지 승격 조건을 다 넘겼어요 — 화면에 올릴지는 용준님이 정해요(자동 승격 없음).">조건 통과</span>}
        {/* ⏸️ 최종 합격 보류 — 결재는 났지만 재검토 때문에 표시를 되돌린 줄. 알림은 화면에 그대로 있다.
            ⚠️ 결재일과 보류일이 **같은 날일 수 있다**(🐋가 그랬다). 그때 날짜를 두 번 읽어 주면
            "8월 22일에 났는데 8월 22일에 멈췄어요"가 되어 사람이 두 번 더듬는다 — 한 번만 쓴다. */}
        {susp && <span className="inline-flex items-center h-5 px-2 rounded-full border border-dc-point/50 bg-dc-point/10 text-[#EBCA96] text-[10.5px] font-semibold"
          title={`${susp.certified_on === susp.suspended_on
            ? `${susp.certified_on}에 최종 합격 결재가 났는데 같은 날 다시 멈췄어요.`
            : `${susp.certified_on}에 최종 합격 결재가 났는데 ${susp.suspended_on}에 잠시 멈췄어요.`} ${susp.why} 알림은 화면에 그대로 있어요 — 내린 게 아니에요.`}>⏸️ 최종 합격 보류</span>}
        {/* ⚡ 조기 판정 — 표본 30을 다 안 채워도 승부가 갈렸으면 여기 깃발이 선다. 깃발일 뿐 자동 승격·강등은 없다.
            ⏸️ 보류 중인 줄엔 이 깃발을 달지 않는다: '조기 합격 안건'과 '최종 합격 보류'가 나란히 서면 서로 반대말처럼
            읽히고(안건은 이미 처리돼 되돌려진 상태다), 배지가 4개가 되어 한 줄에서 위계가 무너진다.
            숨기는 게 아니라 자리를 옮긴 것 — 아래 5줄의 '⚡ 95% 구간 …' 문장은 그대로 나온다. */}
        {early?.flag && !susp && <span className={`inline-flex items-center h-5 px-2 rounded-full border text-[10.5px] font-semibold ${early.flag === 'pass'
          ? 'border-dc-ok/40 bg-dc-ok/[0.07] text-dc-ok' : 'border-dc-down/40 bg-dc-down/[0.07] text-dc-down'}`}
          title={`${early.note} 결재는 용준님이 해요 — 저절로 올라가거나 내려가지 않아요.`}>⚡ {early.label}</span>}
        <span className="ml-auto text-[14px] text-dc-ink2 whitespace-nowrap" title={WIN_TIP}>
          5분{' '}
          <b className={`text-[17.5px] font-bold ${c.win5 == null ? 'text-dc-ink3' : dim ? 'text-dc-ink2' : 'text-dc-ink1'}`}>
            {c.win5 == null ? '—' : `${c.win5}%`}
          </b>
          <span className="text-dc-ink3"> / 기준 {c.bar30}%</span>
        </span>
      </div>

      {/* 2줄 — 승률 막대 + 70% 기준선 */}
      <WinBar win={c.win5} bar={c.bar30} />

      {/* 3줄 — 승격 조건 3개 배지 + 30분 + 최근 5일 추세 */}
      <div className="flex items-center gap-x-2 gap-y-1.5 flex-wrap text-[11.5px]">
        <Gate ok={g.win} label="승률"
          detail={c.win5 == null ? '—' : g.win ? `${c.win5}%` : `−${c.gap}%p`}
          hint={c.win5 == null ? '아직 채점된 울림이 없어 승률이 없어요.'
            : g.win ? `기준 ${c.bar30}%를 넘겼어요.` : `${c.bar30}%까지 ${c.gap}%p 모자라요.`} />
        <Gate ok={g.sample} label="표본"
          detail={g.sample ? `${c.n}건` : `${c.n}/${nMin}`}
          hint={`채점 끝난 울림 ${c.n}건 — 임시 합격 ${c.pct30}% · 최종 합격 ${c.pct100}% 진행`} />
        {/* 늦게 우는 알림은 "맞아도 못 먹는다" — 탈락 상태는 '이미 늦음'이라고 그대로 말한다 */}
        <Gate ok={g.timely} label={g.timely === false ? '이미 늦음' : '늦지 않게 울림'}
          detail={g.timely == null ? '(재는 중)' : ''}
          hint={g.timely == null
            ? '울린 순간에 이미 얼마나 움직였는지(늦은 정도)를 아직 못 쟀어요 — 못 잰 건 통과로 치지 않아요.'
            : `${tm.note} — 울릴 때 이미 움직인 폭이 그 뒤 최대 이익 폭보다 작아야 "먹을 게 남은 알림"이에요.`} />
        <span className="text-dc-ink3" title="울린 지 30분 뒤에도 방향이 유지됐나 — 5분만 좋고 30분에 무너지면 뒷북 알림이에요.">
          30분 <b className={c.win30 == null ? 'text-dc-ink3/70' : hitCls(c.win30)}>{c.win30 == null ? '—' : `${c.win30}%`}</b>
          {c.n30 > 0 && <span className="text-dc-ink3/70"> ({c.n30})</span>}
        </span>
        <span className="ml-auto text-dc-ink3 whitespace-nowrap"
          title={t ? `최근 5일 ${t.win}% (${t.n}건) — 전체보다 ${t.delta > 0 ? '+' : ''}${t.delta}%p`
            : '최근 5일 표본이 얇아 추세를 말하기 일러요.'}>
          최근 5일 {t == null ? <span className="text-dc-ink3/70">—</span>
            : t.dir > 0 ? <b className="text-dc-up">↑ +{t.delta}%p</b>
              : t.dir < 0 ? <b className="text-dc-down">↓ {t.delta}%p</b>
                : <b className="text-dc-ink2">→ 그대로</b>}
        </span>
      </div>

      {/* 🪦 4줄(🎞️ 기록 재생 소급 병기)은 2026-08-30에 뺐다 — 3주 연속 부적격이라 어떤 판정에도 못 쓰는
          숫자였고, 화면에 남겨 두면 사람이 근거처럼 읽게 되는 게 더 큰 위험이었다.
          역할은 바로 아래 💵줄의 **되감기** 칸이 대체한다(흉내가 아니라 실제 울림을 되감아 잰다).
          되살리려면 서버 debut_board._replay_book 주석 해제 + 이 자리 렌더 복원(git 이력에 원본). */}

      {/* 4줄 — 💵 두 번째 잣대(체결가 기준): 울림이 뜬 순간 **실제로 살 수 있던 값**으로 다시 잰 성적.
          위 승률과 나란히만 쓴다 — 합격·재강등 판정에는 아직 안 들어간다(선은 표본이 쌓이면 용준님이 정한다).
          이름은 TERMS.md ③표에 등재된 '체결가 기준 채점' 그대로 — 화면·문서·서버가 한 이름을 쓴다.
          🎞️ 08-30부터 **재는 방식이 둘**이라 한 줄 안에서 갈라 적는다(호가 / 되감기).
            섞어서 한 숫자로 보이면 그게 곧 거짓말이다 — 되감기는 호가보다 후한 자다(스프레드만큼). */}
      {fl && (
        <p className="text-[13px] text-dc-ink2 flex items-center gap-x-2.5 gap-y-1 flex-wrap"
          title={`울림이 화면에 뜬 순간 실제로 살 수 있던 값(최우선 매도호가)에 사서, 5분 뒤 팔 수 있던 값(최우선 매수호가)에 판다고 본 성적이에요.${fl.cost_ref != null ? ` 사고팔 때 드는 돈 왕복 ${fl.cost_ref}%를 뺐어요.` : ''} 위 5분 승률과 기준가가 달라서 숫자가 갈릴 수 있어요 — 아직 합격·재강등 판정에는 쓰지 않아요.`}>
          <span className="text-dc-ink3">💵 체결가 기준</span>
          {/* ⓐ 호가로 잰 값 — 원래 방식. 되감기 칸이 함께 뜰 땐 '호가'라고 이름을 붙여야
              어느 쪽 숫자인지 사람이 가릴 수 있다(칸이 하나뿐이면 이름이 군더더기라 안 붙인다). */}
          <span>
            {tp && <span className="text-dc-ink3">호가 </span>}
            {fl.why || fl.win == null
              ? <b className="text-dc-ink3/70">{tp ? `${fl.n}건` : (fl.why || '—')}</b>
              : <b className={hitCls(fl.win)}>{fl.n}건 {fl.win}%</b>}
          </span>
          {/* 비용 빼기 전 숫자는 **달라질 때만** 보여준다 — 같은 값을 괄호로 한 번 더 쓰면 고장처럼 읽힌다.
              ⚠️ `!fl.why`가 앞에 붙는 이유: 못 재는 줄("재는 중 아님")에 이 괄호만 따라붙으면
              한 줄 안에서 말이 뒤집힌다. 서버(fill_score.board_entry)도 같은 약속을 지키지만,
              화면에서도 한 번 더 막는다 — 이 문장은 사용자가 눈으로 읽는 자리다. */}
          {!fl.why && fl.win != null && fl.win_gross != null && fl.win_gross !== fl.win && (
            <span className="text-dc-ink3">(비용 전 {fl.win_gross}%)</span>
          )}
          {/* ⓑ 🎞️ 되감기로 잰 값 — 울림 순간 호가가 없던 과거 울림을 체결 기록으로 되감아 잰 성적.
              위 호가 칸과 **더하지 않는다**(두 칸은 서로 다른 울림을 센다 — 서버가 겹치지 않게 갈라 준다). */}
          {tp && (
            <span title={`이 줄엔 울림 순간 호가가 남지 않은 옛 울림이 있어요. 그 건들은 같은 시각 체결 기록으로 값을 되감아 쟀어요(사고판 값이 호가가 아니라 체결가예요 — 그만큼 후한 자라 위 호가 숫자와 더하지 않아요).${tp.cost_ref != null ? ` 왕복 ${tp.cost_ref}%는 똑같이 뺐어요.` : ''}`}>
              <span className="text-dc-ink3">되감기 </span>
              {tp.why || tp.win == null
                ? <b className="text-dc-ink3/70">{tp.n}건</b>
                : <b className={hitCls(tp.win)}>{tp.n}건 {tp.win}%</b>}
              {!tp.why && tp.win != null && tp.win_gross != null && tp.win_gross !== tp.win && (
                <span className="text-dc-ink3"> (비용 전 {tp.win_gross}%)</span>
              )}
            </span>
          )}
          <span><span className="text-dc-ink3">공식</span> <b className={c.win5 == null ? 'text-dc-ink3/70' : hitCls(c.win5)}>{c.n}건 {c.win5 == null ? '—' : `${c.win5}%`}</b></span>
          {/* '재는 중' 표시는 **실제로 재고 있는 줄에만**. 바로 앞이 "재는 중 아님(호가 미구독)"인데
              뒤에 '재는 중'을 붙이면 한 줄 안에서 말이 뒤집힌다.
              되감기만 재고 있는 줄(호가 0건)도 '재고 있는 줄'이다 — 그래서 tp의 표본도 함께 본다. */}
          {(!fl.why || (tp && tp.n > 0)) && <span className="inline-flex items-center h-[18px] px-1.5 rounded-[5px] border border-dc-line1 bg-dc-bg1 text-dc-ink3 text-[10.5px]"
            title="이 칸은 아직 재기만 해요 — 합격·재강등 선은 표본이 쌓인 뒤 용준님이 정해요.">재는 중</span>}
        </p>
      )}

      {/* 5줄 — 다음 할 일 한 문장 */}
      <p className={`text-[13px] ${dim ? 'text-dc-ink3' : 'text-dc-ink2'}`}>
        {c.need.what === '표본' && <>👉 {c.need.target}까지 <b className="text-dc-ink1 font-semibold">{c.need.n_left}건</b> 더 쌓으면 돼요.</>}
        {c.need.what === '보류' && <>⏸️ 최종 합격이 잠시 멈춰 있어요 — {c.need.note}</>}
        {c.need.what === '승률' && <>👉 표본은 찼어요 — {c.need.target}까지 승률이 <b className="text-dc-ink1 font-semibold">{c.need.gap}%p</b> 모자라요.</>}
        {c.need.what === '안늦음' && <>👉 승률·표본은 넘겼는데 <b className="text-dc-ink1 font-semibold">늦지 않게 울림</b> 조건이 남았어요 ({c.need.note}).</>}
        {c.need.what === '없음' && <>세 조건을 모두 넘겼어요 — 화면에 올릴지는 <b className="text-dc-ink1 font-semibold">용준님이 정해요</b>.</>}
        {early && <span className="block text-dc-ink3">⚡ {early.note}</span>}
      </p>
    </div>
  );
}

function DebutRace({ b }: { b: DebutBoard }) {
  const cl = b.closest;
  return (
    // 🎨 규격 V2(08-23) — 카드 상자 없음. 섹션은 키커 + 헤어라인 행으로만 구획한다.
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className={KICKER}>🎓 승격 심사</h3>
        <span className={KICKER}>검증 중으로 성적을 쌓는 후보가 화면에 오기까지</span>
      </div>
      {/* 한 줄 요약 — 이 화면에서 제일 먼저 읽을 문장 */}
      <p className="text-[15.5px] text-dc-ink2 leading-relaxed pb-2">
        조건 통과 <b className={b.n_passed > 0 ? 'text-dc-ink1 font-bold' : 'text-dc-ink2'}>{b.n_passed}명</b>
        {cl && <> · 가장 가까운 건 <b className="text-dc-ink1 font-bold">{cl.label}</b>
          <span className="text-dc-ink3"> ({needText(cl.need)})</span></>}
        {/* ⚡ 조기 판정 깃발 — 30건을 다 안 채웠어도 이미 승부가 갈린 후보. 주간 점검에서 제일 먼저 볼 숫자. */}
        {!!b.n_early_pass && <> · <b className="text-dc-point font-semibold">⚡ 조기 합격 안건 {b.n_early_pass}명</b></>}
        {!!b.n_early_demote && <> · <b className="text-dc-down font-semibold">⚡ 조기 재강등 안건 {b.n_early_demote}명</b></>}
      </p>

      <div>
        {b.candidates.map((c) => <RaceRow key={c.id ?? c.kind} c={c} nMin={b.n_provisional} />)}
      </div>

      <p className="text-[13px] text-dc-ink3 leading-relaxed border-t border-dc-hair pt-3">
        화면 승격에 가까운 순으로 줄 세웠어요(제일 막힌 조건까지의 거리). <b className="text-dc-ink3">승격 조건 세 가지</b> —
        5분 승률 {b.bar30}% · 표본 {b.n_provisional}건(최종 합격은 {b.n_certified}건) · 늦지 않게 울림(울릴 때 이미 움직인 폭 &lt; 그 뒤 최대 이익 폭).
        지금 {b.n_candidates}명을 동시에 재는 중이라, 한 명이 우연히 넘길 수도 있어요 — 넘겨도 자동 승격은 없고 용준님이 정해요.
        {b.early_bars && <> <b className="text-dc-ink3">⚡ 조기 판정</b> — 실전 성적의 {b.early_bars.conf}% 폭이
          아래끝 {b.early_bars.pass}% 위면 조기 합격, 위끝 {b.early_bars.demote}% 아래면 조기 재강등을 <b className="text-dc-ink3">안건으로 올려요</b>(정하는 건 용준님).
          결재가 나면 표본 {b.n_certified}건을 다 안 채웠어도 <b className="text-dc-ink3">최종 합격</b>으로 표시돼요 — 성적은 그 뒤로도 계속 채점해요.</>}
        {/* 🪦 08-30 — '🎞️ 기록 재생(소급)' 각주는 뺐다. 서버가 그 값을 더 안 보낸다(위 4줄 주석 참조). */}
        {b.fill_meta && <> 💵 <b className="text-dc-ink3">체결가 기준</b>은 {b.fill_meta.note}
          {' '}사고팔 때 드는 돈은 주식 왕복 {b.fill_meta.cost_stock}% · ETF 왕복 {b.fill_meta.cost_etf}%로 뺐어요
          (ETF는 세금이 없어 더 싸요).</>}
        {b.fill_meta?.tape_note && <> <b className="text-dc-ink3">되감기</b>는 {b.fill_meta.tape_note}</>}
      </p>
    </div>
  );
}

// ═══ ② 📡 오늘 / 📅 지난 날짜·전 기간 — 화면 알림·검증 중 한 표 ═══════════════════════════════════
// 📅 08-04 날짜 탐색 — "오늘만" 보이던 표를 아무 날짜나·전 기간 합산으로 넘겨볼 수 있게.
//   날짜 목록은 **알림 기록에 기록이 있는 날**(서버 days)이라 주말·휴장일은 자연히 건너뛴다.
export type ViewSel = { scope: 'day' | 'all'; date: string };

function DayNav({ sel, days, today, onPick, busy }: {
  sel: ViewSel; days: string[]; today: string; onPick: (s: ViewSel) => void; busy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const list = Array.from(new Set([...(days || []), today])).sort();
  const i = list.indexOf(sel.date);
  const prev = i > 0 ? list[i - 1] : null;
  const next = i >= 0 && i < list.length - 1 ? list[i + 1] : null;
  const all = sel.scope === 'all';
  const btn = GHOST;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button className={btn} disabled={all || !prev} onClick={() => prev && onPick({ scope: 'day', date: prev })}
        title={prev ? `${fmtDay(prev)}로` : '더 이전 기록이 없어요'}>◀ 이전</button>
      <div className="relative">
        <button className={open ? GHOST_ON : btn} onClick={() => setOpen((v) => !v)}
          title="날짜 고르기 — 알림이 기록된 날만 나와요">
          {all ? '전체 기간' : `${fmtDay(sel.date)}${sel.date === today ? ' · 오늘' : ''}`} ▾
        </button>
        {open && (
          <div className="absolute z-20 mt-1 max-h-56 overflow-y-auto dc-scroll rounded-lg border border-dc-line1 bg-dc-bg1 shadow-xl min-w-[124px]">
            {[...list].reverse().map((d) => (
              <button key={d} onClick={() => { onPick({ scope: 'day', date: d }); setOpen(false); }}
                className={`block w-full text-left text-[10.5px] px-2 py-1 whitespace-nowrap hover:bg-dc-bg2
                  ${!all && d === sel.date ? 'text-dc-point' : 'text-dc-ink2'}`}>
                {fmtDay(d)}{d === today && ' · 오늘'}
              </button>
            ))}
          </div>
        )}
      </div>
      <button className={btn} disabled={all || !next} onClick={() => next && onPick({ scope: 'day', date: next })}
        title={next ? `${fmtDay(next)}로` : '가장 최근이에요'}>다음 ▶</button>
      <button className={all ? GHOST_ON : btn}
        onClick={() => onPick(all ? { scope: 'day', date: today } : { scope: 'all', date: today })}
        title="쌓인 모든 날짜를 한 표로 합쳐서 봐요 — 하루치는 표본이 얇아 승률이 요동쳐요">
        {all ? '오늘로 돌아가기' : '전체 기간'}
      </button>
      {!all && sel.date !== today && (
        <button className={btn} onClick={() => onPick({ scope: 'day', date: today })}>오늘</button>
      )}
      {busy && <span className="text-[11.5px] text-dc-ink3/70">불러오는 중…</span>}
    </div>
  );
}

// 세 단계 위계: 5분(판정 기준, 제일 큼) → 30분(뒷북 감별, 중간) → 1·3·10분(참고, 작고 회색·폰에선 숨김).
const TONE = {
  strong: 'text-[13px] font-bold',
  mid: 'text-[13px]',
  faint: 'text-[11.5px] hidden sm:table-cell',
} as const;
function Cell({ hz, tone }: { hz?: AlertLiveHz; tone: keyof typeof TONE }) {
  const base = `py-2.5 px-1.5 text-right ${TONE[tone]}`;
  if (!hz || hz.n === 0) return <td className={`${base} text-dc-ink3/70`}>—</td>;
  return (
    <td className={`${base} ${tone === 'faint' ? 'text-dc-ink3' : hitCls(hz.hit)}`}
      title={`평균 ${signed(hz.avg)} 움직임 · ${hz.n}건 채점 완료`}>
      {hz.hit != null ? `${hz.hit}%` : '—'}{tone === 'strong' && <span className="text-dc-ink3/70 text-[10.5px] font-normal"> ({hz.n})</span>}
    </td>
  );
}

function TodayRow({ k, data, allScope }: { k: string; data: AlertLive; allScope: boolean }) {
  const meta = KIND_META[k];
  const d = data.by_kind[k];
  const unscored = UNSCORED_KINDS.has(k);
  const feed = k === 'news' ? data.news : k === 'lifeline' ? data.lifeline : undefined;
  // fires가 null이면 '0건'이 아니라 **모름**(지난 날짜의 뉴스처럼 셀 기록이 남아 있지 않은 경우) — 0으로 위조하지 않는다.
  const fires: number | null = unscored ? (feed?.fires ?? null) : (d?.fires ?? 0);
  const last = unscored ? (feed?.last_ts ?? null) : (d?.last_ts ?? null);
  const stage: AlertStage = data.stage?.[k] ?? 'silent';
  // 🪦 정리된 종류는 **울린 기록이 남아 있어도** 흐리게 — 지난 날짜를 보면 그날 숫자가 그대로 뜨는데,
  //   밝게 그리면 "지금도 도는 알림"처럼 읽힌다(옆 줄의 검증 중보다 더 눈에 띄는 역전까지 났다).
  return (
    <tr className={`border-t border-dc-hair/80 ${(fires ?? 0) === 0 || stage === 'retired' ? 'opacity-50' : ''}`}>
      <td className="py-2.5 pr-2 whitespace-nowrap text-dc-ink2 text-[13px]" title={meta.hint}>
        {/* 종류는 색 점으로 — 단타 피드 노드·범례와 같은 색 사전(이모지 아이콘 은퇴) */}
        <span className="inline-flex items-center gap-[7px]">
          <span className={`w-2 h-2 rounded-full shrink-0 ${kindDot(k)}`} />{meta.label}
        </span>
      </td>
      <td className="py-2.5 px-1"><StageBadge stage={stage} kind={k} /></td>
      <td className="py-2.5 px-1.5 text-right text-[13px]"
        title={fires == null ? '이 날의 기록이 남아 있지 않아 몇 번 울렸는지 알 수 없어요.' : undefined}>{fires || '—'}</td>
      {unscored
        // ⚠️ '채점 안 함'을 한 칸으로 합치면 폰에서 열이 어긋난다(1·3·10분은 폰에서 숨김) —
        //    보이는 2칸 + 숨는 3칸으로 나눠 두 폭 모두 줄이 맞게.
        ? <>
          <td className="py-2.5 px-2 text-center text-dc-ink3/70 text-[13px]" colSpan={2} title={UNSCORED_TIP[k]}>채점 안 함</td>
          <td className="hidden sm:table-cell" colSpan={3} />
        </>
        : <>
          <Cell hz={d?.hz?.m5} tone="strong" />
          <Cell hz={d?.m30} tone="mid" />
          {MINOR.map((h) => <Cell key={h} hz={d?.hz?.[h]} tone="faint" />)}
        </>}
      {/* 마지막 칸 — 하루를 볼 땐 '마지막으로 울린 시각', 전 기간을 볼 땐 '마지막으로 울린 날' */}
      <td className="py-2.5 pl-2 text-right text-[11.5px] text-dc-ink3 whitespace-nowrap"
        title={allScope && d?.first_date ? `${d.first_date} ~ ${d.last_date} · ${d.n_days ?? 0}일에 걸쳐 울렸어요` : undefined}>
        {allScope ? (d?.last_date ?? '—') : fmtHm(last)}
      </td>
    </tr>
  );
}

function TodayTable({ data, sel, today }: { data: AlertLive; sel: ViewSel; today: string }) {
  const allScope = sel.scope === 'all';
  // 위→아래: 화면에 나가는 것 → 검증 중(아직 재는 중) → 🪦 정리됨(더 안 잼). 눈이 위에서부터 읽으면
  //   '지금 살아 있는 알림'이 먼저 오고, 과거 기록만 남은 줄이 맨 아래로 내려간다.
  const rank = (k: string) => {
    const s = data.stage?.[k] ?? 'silent';
    return s === 'retired' ? 2 : s === 'silent' ? 1 : 0;
  };
  const kinds = [...TABLE_KINDS].sort((a, b) => rank(a) - rank(b) || TABLE_KINDS.indexOf(a) - TABLE_KINDS.indexOf(b));
  const total = kinds.reduce((s, k) => s + (UNSCORED_KINDS.has(k)
    ? ((k === 'news' ? data.news : data.lifeline)?.fires ?? 0) : (data.by_kind[k]?.fires ?? 0)), 0);
  // 빈 표를 '고장'으로 오해하지 않게 — 오늘은 장 진행에 맞춰, 지난 날짜는 그 날 사정에 맞춰 설명한다.
  const emptyCopy = allScope ? '아직 알림 기록에 쌓인 울림이 없어요.'
    : sel.date === today ? EMPTY_COPY[marketPhase()]
      : isWeekendDay(sel.date) ? '이 날은 장이 안 열렸어요(주말) — 표가 비어 있는 게 정상이에요.'
        : '이 날은 울린 알림이 없었어요 (휴장일이었을 수도 있어요).';
  return (
    <div className="space-y-1.5">
      {total === 0 && (
        <p className="text-[13px] text-dc-ink3 py-1">
          {emptyCopy}
        </p>
      )}
      <div className="overflow-x-auto dc-scroll">
        <table className="w-full min-w-[300px] sm:min-w-[520px] text-dc-ink2">
          <thead>
            {/* 표 머리는 키커체(10.5/650/자간 .12em) — 규격 V2의 표 규칙 */}
            <tr className={`${KICKER} border-b border-dc-hair`}>
              <th className="py-2 pr-2 text-left font-[650]">종류</th>
              <th className="py-2 px-1 text-left font-[650]" title="화면·소리·푸시로 나가는가, 아니면 성적만 쌓는 중인가">상태</th>
              <th className="py-2 px-1.5 text-right font-[650]" title={allScope ? '이 알림이 전 기간 동안 울린 총 횟수' : '이 날 이 알림이 울린 횟수'}>울림</th>
              <th className="py-2 px-1.5 text-right font-[650] text-dc-ink2" title={`${WIN_TIP} — 화면 승격 판정은 이 한 칸으로 해요`}>5분</th>
              <th className="py-2 px-1.5 text-right font-[650]" title="30분 뒤에도 방향이 유지됐나 — 5분만 좋고 30분에 무너지면 뒷북 알림이에요">30분</th>
              {MINOR.map((h) => (
                <th key={h} className="py-2 px-1.5 text-right font-[650] hidden sm:table-cell"
                  title={`울린 뒤 ${MINOR_LABEL[h]} 시점 적중률 — 참고용(판정 기준은 5분)`}>{MINOR_LABEL[h]}</th>
              ))}
              <th className="py-2 pl-2 text-right font-[650]">{allScope ? '마지막 날' : '마지막'}</th>
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => <TodayRow key={k} k={k} data={data} allScope={allScope} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 📡 데이터 공급 상태 — 접어 숨기지 않는다(끊기면 성적 전부가 흔들리는 값이라 늘 보이는 자리에).
function HealthLine({ data }: { data: AlertLive }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-dc-ink3 border-t border-dc-hair pt-2.5">
      <span title="오늘 체결 기록(초·틱 단위 원본) 파일 크기 — 채점을 다시 돌려볼 원본이에요">
        🎞️ 기록 <b className={`${data.microtape?.active ? 'text-dc-ink2' : 'text-dc-ink3/70'}`}>{data.microtape?.mb ?? 0}MB</b>
      </span>
      <span title="실시간 가격공급 — 구독 중 종목 수 / 라이브로 값이 들어오는 종목 수">
        📡 가격공급 <b className="text-dc-ink2">{data.ws?.n_subscribed ?? 0}</b>{data.ws?.n_desired ? `/${data.ws.n_desired}` : ''}
        <span className="text-dc-ink3/70"> · 수신 </span><span>{data.ws?.n_live ?? 0}</span>
        <span className={`ml-1 ${data.ws?.started ? 'text-dc-ok' : 'text-dc-ink3/70'}`}>●</span>
      </span>
    </div>
  );
}

// ═══ ③ 🌊 복기·정밀도(접힘) ═════════════════════════════════════════
// 하이닉스 힘 감지 vs 가격경보 대조 그래프(누적 울림·적중, 5분 기준) — 기존 SVG 스파크라인 기법
function DualSpark({ tl }: { tl: AlertLive['timeline'] }) {
  const n = tl.labels.length;
  const w = 300, h = 56;
  const max = Math.max(1, ...tl.knee.fires, ...tl.stockmove.fires);
  const X = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * w);
  const Y = (v: number) => h - (v / max) * h;
  const path = (arr: number[]) => arr.map((v, i) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ');
  const kFire = tl.knee.fires[n - 1] ?? 0, kHit = tl.knee.hits[n - 1] ?? 0;
  const sFire = tl.stockmove.fires[n - 1] ?? 0, sHit = tl.stockmove.hits[n - 1] ?? 0;
  const empty = kFire === 0 && sFire === 0;
  return (
    <div>
      <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap text-[11.5px] mb-1">
        <span className="text-dc-point">🦵 하이닉스 힘 감지 <b>{kFire}</b>번 울림 · 적중 <b>{kHit}</b></span>
        <span className="text-dc-ink2">🎯 가격 경보 <b>{sFire}</b>번 울림 · 적중 <b>{sHit}</b></span>
        <span className="ml-auto text-dc-ink3/70">실선=울림 · 점선=적중(5분·누적)</span>
      </div>
      {empty ? (
        <p className="text-[10.5px] text-dc-ink3/70 py-3 text-center">오늘 아직 두 알림 모두 울린 적이 없어요.</p>
      ) : (
        <>
          <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-14">
            <polyline points={path(tl.knee.fires)} fill="none" className="stroke-dc-point" strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
            <polyline points={path(tl.knee.hits)} fill="none" className="stroke-dc-point/60" strokeWidth={1.2} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
            <polyline points={path(tl.stockmove.fires)} fill="none" className="stroke-dc-ink2" strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
            <polyline points={path(tl.stockmove.hits)} fill="none" className="stroke-dc-ink2/60" strokeWidth={1.2} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="flex justify-between text-[11.5px] text-dc-ink3/70 mt-0.5">
            <span>{tl.labels[0]}</span>
            <span>{tl.labels[n - 1]}</span>
          </div>
        </>
      )}
    </div>
  );
}

const REASON_CLS: Record<string, string> = {
  '감지 사각': 'border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn',
  '데이터 늦음': 'border-dc-line1 bg-dc-bg2/40 text-dc-ink2',
  '문턱 미달': 'border-dc-line1 bg-dc-bg1 text-dc-ink3',
};
function waveRows(rev: WaveReview): { market: string; w: WaveItem }[] {
  const out: { market: string; w: WaveItem }[] = [];
  for (const [mk, arr] of Object.entries(rev.waves || {})) {
    for (const w of arr) out.push({ market: mk === '000660' ? '하이닉스' : mk, w });
  }
  out.sort((a, b) => (Number(b.w.silent) - Number(a.w.silent)) || (Math.abs(b.w.size) - Math.abs(a.w.size)));
  return out;
}
function WavePanel({ rev }: { rev: WaveReview }) {
  const rows = waveRows(rev);
  const shown = rows.slice(0, 8);
  const byReason = Object.entries(rev.summary?.by_reason ?? {}).map(([r, n]) => `${r} ${n}`).join(' · ');
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[10.5px]">
        <b className="text-dc-ink2">{rev.date}</b>
        <span className="text-dc-ink3">파도 {rev.summary?.n_waves ?? 0}개 · 침묵 <b className={rev.summary?.n_silent ? 'text-dc-up' : 'text-dc-ink2'}>{rev.summary?.n_silent ?? 0}</b></span>
        {byReason && <span className="text-dc-ink3/70">({byReason})</span>}
        <span className="ml-auto text-[11.5px] text-dc-ink3/70">{rev.source === 'live' ? '오늘·즉석' : '마감 저장'}</span>
      </div>
      {shown.length === 0 ? (
        <p className="text-[11.5px] text-dc-ink3/70">이 날은 잔물결뿐 — 잡을 만한 파도가 없었어요.</p>
      ) : (
        <div className="overflow-x-auto dc-scroll">
          <table className="w-full min-w-[300px] sm:min-w-[440px] text-[10.5px]">
            <tbody>
              {shown.map(({ market, w }, i) => {
                const up = w.dir > 0;
                return (
                  <tr key={i} className="border-t border-dc-hair">
                    <td className="py-1 pr-2 whitespace-nowrap text-dc-ink3">{w.start.hm}→{w.end.hm}</td>
                    <td className="py-1 px-1 whitespace-nowrap text-dc-ink3">{market}</td>
                    <td className={`py-1 px-2 whitespace-nowrap ${up ? 'text-dc-up' : 'text-dc-down'}`}>
                      {up ? '▲' : '▼'} {w.size > 0 ? '+' : ''}{w.size}{w.unit}{w.fast && <span className="ml-1 text-dc-point" title="30분 내 급했던 파도">⚡</span>}
                    </td>
                    <td className="py-1 pl-2 text-right">
                      {w.silent ? (
                        <span className={`text-[11.5px] px-1.5 py-px rounded-full border ${REASON_CLS[w.silence_reason ?? ''] ?? 'border-dc-line1 text-dc-ink3'}`} title={w.silence_detail}>
                          🔇 {w.silence_reason ?? '침묵'}
                        </span>
                      ) : (
                        <span className="text-[11.5px] text-dc-ok" title={w.alerts.map((a) => `${a.hm} ${a.kind}`).join('\n')}>✓ 대응 {w.alerts.length}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > shown.length && <p className="text-[11.5px] text-dc-ink3/70 pt-0.5">그 밖에 작은 파도 {rows.length - shown.length}개 생략</p>}
        </div>
      )}
    </div>
  );
}

// 🌊 복기·정밀도 — 기본 닫힘. **열 때 처음 불러온다**(닫아두면 복기 API를 아예 안 부른다).
function ReviewSection({ data }: { data: AlertLive }) {
  const [open, setOpen] = useState(false);
  const [revs, setRevs] = useState<WaveReview[] | null>(null);

  useEffect(() => {
    if (!open || revs) return;
    let alive = true;
    Promise.all([
      fetchWaveReview(kstDateStr(0), true),
      fetchWaveReview(kstDateStr(-1)).then((r) => r ?? fetchWaveReview()),
    ]).then(([t, p]) => {
      if (!alive) return;
      const out: WaveReview[] = [];
      for (const r of [t, p]) if (r && !out.some((x) => x.date === r.date)) out.push(r);
      setRevs(out);
    }).catch(() => { if (alive) setRevs([]); });
    return () => { alive = false; };
  }, [open, revs]);

  return (
    <div className="border-t border-dc-hair">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 py-3 text-left">
        <span className="text-[14px] font-bold text-dc-ink1">🌊 복기 · 채점 정밀도</span>
        <span className="text-[11.5px] text-dc-ink3">놓친 파도 · 하이닉스 힘 감지↔가격 경보 대조 · 성적을 어떻게 쟀나</span>
        <span className="ml-auto text-[11.5px] text-dc-point">{open ? '▾ 접기' : '▸ 펼치기'}</span>
      </button>
      {open && (
        <div className="pb-3 space-y-3">
          <div>
            <p className={`${KICKER} mb-1.5`}>🌊 파도 복기 — 오늘·어제 놓친 파도가 있었나</p>
            {revs == null ? <p className="text-[13px] text-dc-ink3">불러오는 중…</p>
              : revs.length > 0 ? revs.map((r) => <WavePanel key={r.date} rev={r} />)
                : <p className="text-[13px] text-dc-ink3">복기 데이터가 아직 없어요 (오늘분은 즉석 생성, 어제분은 마감 저장분).</p>}
          </div>
          <div className="border-t border-dc-hair pt-3">
            <p className={`${KICKER} mb-1.5`}>🦵 하이닉스 힘 감지 vs 🎯 가격 경보 — 화면 자리를 주고받은 두 알림의 오늘 누적(5분)</p>
            <DualSpark tl={data.timeline} />
          </div>
          <p className="text-[13px] text-dc-ink3 leading-relaxed border-t border-dc-hair pt-3">
            채점 정밀도(기록 재생): 종목(하이닉스) {data.prec?.stock ?? '체결가'} · 지수 {data.prec?.index ?? '30초 틱'}.
            울린 순간 대비 1/3/5/10분 뒤 실제 가격에서 잰 값이에요(30분은 90초 폴 값이라 조금 더 거칠어요).
          </p>
        </div>
      )}
    </div>
  );
}

// ═══ 컨테이너 — 1층 스크롤 ══════════════════════════════════════════
export default function AlertLivePanel() {
  const today = kstDateStr(0);
  const [data, setData] = useState<AlertLive | null>(null);       // 오늘 라이브 — ①승격 심사·📡건강줄의 원천
  const [err, setErr] = useState(false);
  const [sel, setSel] = useState<ViewSel>({ scope: 'day', date: today });   // 📅 ② 표가 지금 보고 있는 구간
  const [view, setView] = useState<AlertLive | null>(null);       // 지난 날짜·전 기간 응답(오늘이면 안 씀)
  const [busy, setBusy] = useState(false);
  const [viewErr, setViewErr] = useState(false);
  const isToday = sel.scope === 'day' && sel.date === today;

  useEffect(() => {
    let alive = true;
    const load = () => fetchAlertLive().then((d) => { if (alive) { setData(d); setErr(false); } }).catch(() => { if (alive) setErr(true); });
    load();
    // 장중 30초 갱신(마감 후 idle·개장 시 자동 재개). 📅 지난 날짜·전 기간을 보는 동안엔 멈춘다 —
    //   그 화면은 이미 지나간 정적 자료라 갱신할 게 없고, 보고 있는 중에 표가 바뀌면 오히려 헷갈린다.
    const iv = setInterval(() => { if (isMarketHours() && isToday) load(); }, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [isToday]);

  useEffect(() => {   // 📅 선택한 구간 불러오기(오늘이면 위 라이브 응답을 그대로 쓴다 — 중복 호출 없음)
    if (isToday) { setView(null); setViewErr(false); return; }
    let alive = true;
    setBusy(true);
    fetchAlertLive(sel.scope === 'all' ? { scope: 'all' } : { date: sel.date })
      .then((d) => { if (alive) { setView(d); setViewErr(false); } })
      .catch(() => { if (alive) { setView(null); setViewErr(true); } })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [sel, isToday]);

  const shown = isToday ? data : view;
  const title = sel.scope === 'all' ? '📅 전체 기간' : isToday ? '📡 오늘' : '📅 지난 날짜';
  const sub = !shown ? '' : sel.scope === 'all'
    ? `${shown.date} · ${shown.n_days ?? shown.days?.length ?? 0}일 합산`
    : isToday
      ? `${shown.date} · ${new Date(shown.updated_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Asia/Seoul' })}`
      : `${shown.date} · 마감 저장분`;

  return (
    // 🎨 규격 V2(08-23) — 카드 상자를 걷어낸 1층 콘솔. 섹션 구획은 헤어라인 한 줄로만.
    <div className="dc-root dc-in space-y-5">
      {err && <p className="text-[13px] text-dc-warn">성적 데이터를 불러오지 못했어요 — 잠시 뒤 자동 재시도.</p>}
      {!data && !err && <p className="text-[13px] text-dc-ink3">불러오는 중…</p>}

      {/* ① 🎓 승격 심사 — 주인공(누적 성적이라 날짜와 무관하게 늘 최신) */}
      {data?.debut && <DebutRace b={data.debut} />}

      {/* ② 📡 오늘 / 📅 지난 날짜·전 기간 — 화면 알림·검증 중 한 표 */}
      {data && (
        <div className="border-t border-dc-hair pt-4 space-y-2.5">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h3 className={KICKER}>{title}</h3>
            <span className={KICKER}>{sub}</span>
            {isToday && <span className="ml-auto text-[11.5px] text-dc-ink3">장중 30초 자동 갱신</span>}
          </div>
          <DayNav sel={sel} days={data.days ?? []} today={today} onPick={setSel} busy={busy} />
          {viewErr && <p className="text-[13px] text-dc-warn">이 구간 성적을 불러오지 못했어요.</p>}
          {shown ? <TodayTable data={shown} sel={sel} today={today} />
            : !viewErr && <p className="text-[13px] text-dc-ink3">불러오는 중…</p>}
          {/* 🎞️📡 기록량·가격공급은 '지금'의 상태라 오늘 볼 때만(지난 날짜에 오늘 숫자를 붙이면 거짓말이 된다) */}
          {isToday && <HealthLine data={data} />}
        </div>
      )}

      {/* ③ 🌊 복기·정밀도 — 접힘 */}
      {data && <ReviewSection data={data} />}
    </div>
  );
}
